import { Form, Formik } from "formik";
import { Box, Grid, Hidden, Typography } from "@material-ui/core";
import FormAutoComplete, {
  FormSelect,
} from "../../../../forms/formik/FormSelectField";
import FormTextInput from "../../../../forms/formik/FormTextInput";

import CustomButton from "../../../../forms/CustomButton";
import SubmitButton from "../../../../forms/formik/FormSubmitButton";
import InvestorDetailsPreview from "../../InvestorDetails";
import { convertToInr } from "../../../../../utils/cart";
import useStp, { Values } from "./useStp";
import { FormDateInput } from "../../../../forms/formik/FormDateField";
import GridContainer from "../../../../../lib/GridContainer";

export enum Frequency {
  D = "DAILY",
  W = "WEEKLY",
  F = "FORTNIGHTLY",
  M = "MONTHLY",
  Q = "QUARTERLY",
  H = "HALFYEARLY",
  Y = "YEARLY",
}

const EDELWEISSFUND_CODE = "118";

export const STP = () => {
  const {
    initialState,
    validationSchema,
    isEdit,
    onSubmit,
    onAddTransaction,
    changeHandlers,
    options,
    investorPreview,
    goToCart,
    dateProps,
  } = useStp();

  return (
    <Grid container>
      <Grid item lg={9} sm={12}>
        <Box
          sx={{
            margin: "40px",
            marginRight: "10px",
            bgcolor: "white",
            padding: "18px 24px 18px 24px",
            boxShadow: "0px 0px 8px 0px rgba(202, 202, 202, 1);",
            borderRadius: "4px",
          }}
        >
          <Formik<Values>
            initialValues={initialState}
            validationSchema={validationSchema}
            onSubmit={isEdit ? onSubmit : onAddTransaction}
            validateOnBlur
            enableReinitialize
          >
            {({ values, setValues, ...rest }) => (
              <Form>
                <Typography
                  component="h4"
                  style={{
                    fontWeight: "800",
                    fontSize: "24px",
                    marginBottom: "20px",
                    fontFamily: "OpenSans-SemiBold",
                  }}
                >
                  STP
                </Typography>
                <FormAutoComplete
                  name="amc"
                  label={"AMC"}
                  disabled={isEdit}
                  options={options.fundOptions}
                  onChange={changeHandlers.amc(values, setValues)}
                />
                <FormSelect
                  name="folio"
                  label="Folio"
                  disabled={!values.amc}
                  options={options.folioOptions}
                  onChange={changeHandlers.folio(values, setValues)}
                />
                <FormAutoComplete
                  name="switchOutScheme"
                  label="Transfer From Scheme"
                  disabled={!values.folio}
                  options={options.switchOutOptions}
                  onChange={changeHandlers.switchOut(values, setValues)}
                />

                <FormAutoComplete
                  name="category"
                  label="Category"
                  disabled={!values.switchOutScheme}
                  options={options.categoryOptions}
                  onChange={changeHandlers.category(values, setValues)}
                />
                <FormAutoComplete
                  name="switchInScheme"
                  label="Transfer To Scheme"
                  disabled={!values.category}
                  options={options.switchInOptions}
                  onChange={changeHandlers.switchIn(values, setValues)}
                />

                <FormAutoComplete
                  name="stpFrequency"
                  label="STP Frequency"
                  options={options.frequencyOptions}
                  disabled={!values.switchInScheme}
                  onChange={changeHandlers.frequency(values, setValues)}
                />

                <Typography>Time Period</Typography>

                <GridContainer>
                  <Box display="flex" flexDirection="column">
                    <FormDateInput
                      name="stpStartDate"
                      label="STP Start Date"
                      {...dateProps.startDate}
                      disablePast
                      onChange={changeHandlers.handleStartDateChange(values, {
                        ...rest,
                        setValues,
                      })}
                      shouldDisableDate={
                        values.amc === EDELWEISSFUND_CODE &&
                        values.stpFrequency === Frequency.W
                          ? () => false
                          : disableDate
                      }
                    />
                    {values.minimumInstallments ? (
                      <Typography
                        variant="caption"
                        style={{
                          marginTop: "1rem",
                          fontWeight: "bolder",
                          color: "rgb(0, 61, 120)",
                        }}
                      >
                        Minimum Installments :{" "}
                        <strong>{values.minimumInstallments}</strong>
                      </Typography>
                    ) : null}
                  </Box>

                  <Box display="flex" flexDirection="column">
                    <FormDateInput
                      name="stpEndDate"
                      label="STP End Date"
                      {...dateProps.endDate}
                      disablePast
                      onChange={changeHandlers.handleEndDateChange(values, {
                        ...rest,
                        setValues,
                      })}
                      disabled={!values.stpStartDate}
                      shouldDisableDate={
                        values.stpFrequency === Frequency.D
                          ? disableDate
                          : () => false
                      }
                    />
                  </Box>
                </GridContainer>

                <FormAutoComplete
                  name="stpOption"
                  label="STP Option"
                  options={options.stpOptions}
                />

                {values.stpOption === "F" && (
                  <FormTextInput
                    name="amount"
                    label={
                      `Investment Amount ` +
                      (!!values.minAmount
                        ? `(Minimum ${convertToInr(values.minAmount)})`
                        : "")
                    }
                    type="number"
                  />
                )}

                <Box display="flex">
                  <CustomButton
                    label="Go To Cart"
                    variant="outlined"
                    onClick={goToCart}
                  />
                  <SubmitButton
                    label={isEdit ? "Update" : "Add To Cart"}
                    variant="contained"
                  />
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
      <Hidden mdDown>
        <Grid item lg={3}>
          <InvestorDetailsPreview investorData={investorPreview} />
        </Grid>
      </Hidden>
    </Grid>
  );
};

const disableDate = (date: Date) =>
  [0, 6].includes(date.getDay()) || date.getDate() > 28;
